"use client";

import type { RevenueIncomeRow } from "@/lib/types/financial-snapshot";
import { axisLabel, niceScale, CHART_PADDING, CHART_HEIGHT, YAxis, XAxisLabels, ChartSvg } from "../ChartAxes";
import { useChartHover } from "../hover/use-chart-hover";
import { HoverHitAreas } from "../hover/HoverHitAreas";
import { HoverCrosshair } from "../hover/HoverCrosshair";
import { ChartTooltip } from "../hover/ChartTooltip";
import type { HoverPoint } from "../hover/types";

const VIEW_WIDTH = 640;
const BAR_GAP = 6;

const fmtMoney = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toFixed(0)}`;
};

export function RevenueIncomeChart({ rows }: { rows: RevenueIncomeRow[] }) {
  const { containerRef, activeIndex, handlePointerMove, handlePointerLeave, handlePointerUp } = useChartHover();

  const usable = rows.filter((r) => r.revenue != null || r.netIncome != null);
  if (usable.length === 0) return null;

  const allValues = usable.flatMap((r) => [r.revenue, r.netIncome].filter((v): v is number => v != null));
  const maxVal = Math.max(...allValues, 0);
  const minVal = Math.min(...allValues, 0);
  const scale = niceScale(maxVal, minVal);

  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const zeroY = plotBottom - ((0 - scale.min) / (scale.max - scale.min)) * plotHeight;
  const yScale = (v: number) => plotBottom - ((v - scale.min) / (scale.max - scale.min)) * plotHeight;

  const groupWidth = (VIEW_WIDTH - CHART_PADDING.left - CHART_PADDING.right) / usable.length;
  const barWidth = Math.max(6, groupWidth / 2 - BAR_GAP);
  const xScale = (i: number) => CHART_PADDING.left + i * groupWidth + groupWidth / 2;

  const anchors = usable.map((_, i) => xScale(i));

  const points: HoverPoint[] = usable.map((row) => ({
    period: row.period,
    series: [
      { label: "Revenue", value: row.revenue, format: fmtMoney, colorClass: "bg-blue" },
      { label: "Net Income", value: row.netIncome, format: fmtMoney, colorClass: "bg-cgreen" },
    ],
  }));

  return (
    <div ref={containerRef} className="relative">
      <ChartSvg viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}>
        <YAxis ticks={scale.ticks} scale={yScale} format={axisLabel} width={VIEW_WIDTH} />
        {usable.map((row, i) => {
          const groupX = xScale(i);
          const revX = groupX - BAR_GAP / 2 - barWidth;
          const incX = groupX + BAR_GAP / 2;
          return (
            <g key={row.period}>
              {row.revenue != null && (
                <rect
                  x={revX}
                  y={Math.min(yScale(row.revenue), zeroY)}
                  width={barWidth}
                  height={Math.abs(yScale(row.revenue) - zeroY)}
                  className="fill-blue"
                  rx={2}
                />
              )}
              {row.netIncome != null && (
                <rect
                  x={incX}
                  y={Math.min(yScale(row.netIncome), zeroY)}
                  width={barWidth}
                  height={Math.abs(yScale(row.netIncome) - zeroY)}
                  className={row.netIncome >= 0 ? "fill-cgreen" : "fill-cred"}
                  rx={2}
                />
              )}
            </g>
          );
        })}
        <XAxisLabels labels={usable.map((r) => r.period)} xScale={xScale} y={CHART_HEIGHT - 8} />
        {activeIndex != null && <HoverCrosshair x={anchors[activeIndex]} plotTop={plotTop} plotBottom={plotBottom} />}
        <HoverHitAreas
          anchors={anchors}
          viewBoxWidth={VIEW_WIDTH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          onActivate={handlePointerMove}
          onLeave={handlePointerLeave}
          onTap={handlePointerUp}
        />
      </ChartSvg>
      {activeIndex != null && (
        <ChartTooltip point={points[activeIndex]} anchorPct={(anchors[activeIndex] / VIEW_WIDTH) * 100} />
      )}
    </div>
  );
}
